import { useEffect, useRef, useState } from 'react';
import styles from './DownloadModal.module.css';

interface DownloadModalProps {
  url: string;
  fileName: string;
  fileSizeMb?: number | null;
  durationMs?: number | null;
  onClose: () => void;
}

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

export default function DownloadModal({
  url,
  fileName,
  fileSizeMb,
  durationMs,
  onClose,
}: DownloadModalProps) {
  const [downloading, setDownloading] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      onClose();
    } catch {
      // Fallback: direct anchor download (works for same-origin / pre-signed URLs)
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      onClose();
    } finally {
      setDownloading(false);
    }
  };

  // Derive a clean display name (strip extension for title, keep for filename)
  const displayName = fileName.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
  const ext = fileName.split('.').pop()?.toUpperCase() ?? 'MP4';

  return (
    <div
      className={styles.backdrop}
      ref={backdropRef}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Download video"
    >
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.headerTitle}>Download video</span>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* File info card */}
        <div className={styles.fileCard}>
          <div className={styles.fileIconWrap}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="23 7 16 1 1 1 1 23 23 23 23 7"/>
              <polyline points="16 1 16 7 23 7"/>
              <line x1="8" y1="13" x2="16" y2="13"/>
              <line x1="8" y1="17" x2="13" y2="17"/>
            </svg>
            <span className={styles.fileExt}>{ext}</span>
          </div>
          <div className={styles.fileMeta}>
            <span className={styles.fileTitle}>{displayName}</span>
            <span className={styles.fileDetails}>
              {fileSizeMb != null && fileSizeMb > 0 && (
                <span>{fileSizeMb.toFixed(1)} MB</span>
              )}
              {fileSizeMb != null && fileSizeMb > 0 && durationMs != null && durationMs > 0 && (
                <span className={styles.dot}>·</span>
              )}
              {durationMs != null && durationMs > 0 && (
                <span>{formatDuration(durationMs)}</span>
              )}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={downloading}>
            Cancel
          </button>
          <button className={styles.downloadBtn} onClick={handleDownload} disabled={downloading}>
            {downloading ? (
              <>
                <span className={styles.spinner} aria-hidden="true" />
                Downloading…
              </>
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
